import { createHash, createPublicKey, verify } from "node:crypto";
import type { ConnectParams } from "@mewbleh/nori-protocol";

const ED25519_SPKI_PREFIX = Buffer.from("302a300506032b6570032100", "hex");

function decode(value: string): Buffer {
  return Buffer.from(value.replaceAll("-", "+").replaceAll("_", "/"), "base64");
}

function rawPublicKey(value: string): Buffer {
  if (value.includes("BEGIN")) {
    const der = createPublicKey(value).export({ format: "der", type: "spki" }) as Buffer;
    return der.subarray(der.length - 32);
  }
  const raw = decode(value);
  if (raw.length !== 32) throw new Error("Ed25519 public keys must contain 32 raw bytes");
  return raw;
}

export function deriveDeviceId(publicKey: string): string | undefined {
  try { return createHash("sha256").update(rawPublicKey(publicKey)).digest("hex"); }
  catch { return undefined; }
}

function verifySignature(publicKey: string, payload: string, signature: string): boolean {
  try {
    const key = publicKey.includes("BEGIN")
      ? createPublicKey(publicKey)
      : createPublicKey({ key: Buffer.concat([ED25519_SPKI_PREFIX, rawPublicKey(publicKey)]), type: "spki", format: "der" });
    return verify(null, Buffer.from(payload), key, decode(signature));
  } catch {
    return false;
  }
}

export function verifyConnectDevice(params: ConnectParams, nonce: string, token?: string): { ok: true } | { ok: false; message: string } {
  const device = params.device;
  if (!device) return { ok: false, message: "Device identity is required" };
  if (deriveDeviceId(device.publicKey) !== device.id) return { ok: false, message: "Device id does not match its public key" };
  if (Math.abs(Date.now() - device.signedAt) > 5 * 60_000) return { ok: false, message: "Device signature expired" };
  if (device.nonce !== nonce) return { ok: false, message: "Device signature nonce mismatch" };
  const role = params.role ?? "operator";
  const scopes = params.scopes ?? [];
  const base = [device.id, params.client.id, params.client.mode, role, scopes.join(","), String(device.signedAt), token ?? "", nonce];
  const v3 = ["v3", ...base, params.client.platform.trim().toLowerCase(), ""].join("|");
  const v2 = ["v2", ...base].join("|");
  return verifySignature(device.publicKey, v3, device.signature) || verifySignature(device.publicKey, v2, device.signature)
    ? { ok: true }
    : { ok: false, message: "Device signature invalid" };
}
